import React from "react"
import { observer } from "mobx-react-lite"
import { ErrorBoundary } from "react-error-boundary"

import NotFound from "@components/ui/NotFound/NotFound"
import Button from "@components/ui/Button/Button"
import { WorkbookSuggestionOption } from "@store/search/types"

import ProductRecommenderForm from "./ProductRecommenderForm"
import ProductComparisonForm from "./ProductComparisonForm"
import ProductPositioningForm from "./ProductPositioningForm"

import styles from "./WorkbookReportTemplate.module.sass"

export type ReportRequest = {
  id: string
  query: string
  data: any
}

export interface WorkbookReportTemplateProps {
  loading?: boolean
  reportSuggestion: WorkbookSuggestionOption
  onSubmit?: () => void
  onCancel?: () => void
}

const WorkbookReportTemplate: React.FC<WorkbookReportTemplateProps> = observer(
  ({ reportSuggestion, loading, onSubmit, onCancel }) => {
    const handleSubmit = onSubmit
    const handleCancel = onCancel

    const renderForm = () => {
      const suggestionData = reportSuggestion.data
      const formKey = JSON.stringify(suggestionData)

      if (reportSuggestion.type === "PRODUCTRECOMMENDATION") {
        return (
          <ProductRecommenderForm
            initialValues={suggestionData}
            loading={loading}
            onSubmit={handleSubmit}
            onClose={handleCancel}
            key={formKey}
          />
        )
      }

      if (reportSuggestion.type === "PRODUCTCOMPARISON") {
        return (
          <ProductComparisonForm
            initialData={suggestionData}
            loading={loading}
            onSubmit={handleSubmit}
            onClose={handleCancel}
            key={formKey}
          />
        )
      }

      if (reportSuggestion.type === "PRODUCTPOSITIONING") {
        return (
          <ProductPositioningForm
            initialData={suggestionData}
            loading={loading}
            onSubmit={handleSubmit}
            onClose={handleCancel}
            key={formKey}
          />
        )
      }

      throw new Error("Not exists")
    }

    return (
      <div className={styles.root}>
        <ErrorBoundary
          fallback={
            <NotFound
              className={styles.placeholder}
              retryButton={
                <Button size="big" variant="outlined" onClick={handleCancel}>
                  Ok
                </Button>
              }
            >
              Failed to load workflow template
              <br />
              Please reach out to an administrator to get more details
            </NotFound>
          }
        >
          {renderForm()}
        </ErrorBoundary>
      </div>
    )
  }
)

export default WorkbookReportTemplate
